import React from "react";
import {
  CardContent,
  Typography,
  Box,
  CardActionArea,
  CircularProgress,
} from "@mui/material";
import { Chip, Card } from "@/components/atoms";
import { Company } from "@/types/company";

interface CompanyCardProps {
  company: Company;
  onClick: () => void;
  loading: boolean;
  error?: string | null;
}

export const CompanyCard: React.FC<CompanyCardProps> = ({
  company,
  onClick,
  loading,
  error,
}) => {
  const companyId = company.id;

  const MAX_CHIPS = 5;
  const domains = company?.healthcare_domains || [];
  const displayedDomains: string[] = domains.slice(0, MAX_CHIPS);
  const extraCount = domains.length - MAX_CHIPS;
  const componentName = `directory-company-${companyId}`;

  if (loading) {
    return (
      <Card componentName={`${componentName}-loading`}>
        <CircularProgress />
      </Card>
    );
  }

  if (error || !company) {
    return (
      <Card componentName={`${componentName}-error`}>
        <Typography color="error">Failed to load company details.</Typography>
      </Card>
    );
  }

  return (
    <Card
      componentName={componentName}
      sx={{
        flexDirection: "column",
        justifyContent: "space-between",
        textAlign: "left",
        cursor: "pointer",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 4,
        },
      }}
      onClick={onClick}
    >
      <CardActionArea
        sx={{
          "& .MuiCardActionArea-focusHighlight": {
            backgroundColor: "transparent",
          },
        }}
        >
        <CardContent
          sx={{
            p: 0,
            alignItems: "flex-start",
            "&:last-child": { pb: 0 },
            width: "100%",
          }}
        >
          <Box sx={{ position: "absolute", top: 8, right: 8 }}>
            <Chip
              componentName={`${componentName}-operating-status`}
              value={company.operating_status ? "Active" : "Inactive"}
              sx={{
                borderColor: company.operating_status
                  ? "success.main"
                  : "error.main",
                color: company.operating_status ? "success.main" : "error.main",
                boxShadow: 1,
              }}
            />
          </Box>

          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            sx={{ width: "100%" }}
          >
            <img
              src={company.logo_url || undefined}
              alt={`${company.company_name}-logo`}
              style={{
                width: 50,
                height: 50,
                borderRadius: "8px",
                objectFit: "cover",
              }}
            />
          </Box>

          <Box
            display="flex"
            alignItems="flex-start"
            mt={1}
            sx={{ width: "100%" }}
          >
            <Typography variant="h6" fontWeight="bold">
              {company.company_name}
            </Typography>
            <Typography variant="h6" mx={1} color="text.secondary">
              |
            </Typography>
            <Box>
              <Typography
                color="text.secondary"
                sx={{ fontSize: 12, lineHeight: 1 }}
              >
                {company.company_size || "NA"}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 10 }}>
                employees
              </Typography>
            </Box>
          </Box>

          <Box
            display="flex"
            flexWrap="wrap"
            mt={2}
            sx={{
              gap: 0.5,
              "& > li": {
                p: 0,
                m: 0,
                display: "inline-flex",
                width: "auto",
                listStyle: "none",
              },
              width: "100%",
            }}
          >
            {displayedDomains.map((domain: string, index: number) => (
              <Chip
                key={index}
                componentName={componentName}
                value={domain}
                sx={{ boxShadow: 1 }}
              />
            ))}
            {extraCount > 0 && (
              <Chip
                componentName={componentName}
                value="..."
                sx={{ boxShadow: 1 }}
              />
            )}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
